import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Loader2, Mail, RefreshCw, ArrowLeft, KeyRound } from "lucide-react";

const OTP_LEN = 6;

export default function VerificarCorreo() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const email = params.get("email") || "";
  const purpose = params.get("purpose") || "register";

  const [digits, setDigits] = useState(() => Array(OTP_LEN).fill(""));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const code = useMemo(() => digits.join(""), [digits]);

  const setDigit = (idx, val) => {
    const clean = val.replace(/\D/g, "").slice(0, 1);
    setDigits((d) => {
      const next = [...d];
      next[idx] = clean;
      return next;
    });
    if (clean && idx < OTP_LEN - 1) {
      inputs.current[idx + 1]?.focus();
    }
  };

  const handleKey = (idx) => (e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) inputs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < OTP_LEN - 1) inputs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text").trim();
    if (/^\d{6}$/.test(text)) {
      e.preventDefault();
      setDigits(text.split(""));
      inputs.current[OTP_LEN - 1]?.focus();
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (code.length !== OTP_LEN) {
      setError("Ingresa los 6 dígitos.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verificar-otp", { email, code, purpose });
      login(data.access_token, data.user);
      toast.success("¡Verificado! Ingresando a tu cuenta…");
      const target =
        data.user.role === "admin"
          ? "/admin"
          : data.user.role === "medico"
          ? "/medico"
          : "/dashboard";
      navigate(target, { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const { data } = await api.post("/auth/reenviar-otp", { email, purpose });
      if (data?.email_delivered) {
        toast.success("Nuevo código enviado a tu correo.");
      } else {
        toast.warning(
          data?.email_error
            ? "Free-tier de Resend: solo entrega al dueño de la cuenta. Usa el hint TESTING_MODE."
            : "Nuevo código generado."
        );
      }
      setDigits(Array(OTP_LEN).fill(""));
      inputs.current[0]?.focus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
              <KeyRound size={12} />
              {purpose === "login" ? "Paso 2 de 2 · Código" : "Verifica tu correo"}
            </div>
            <h1 className="mt-3 font-display text-3xl font-semibold text-[#0F2942] tracking-tight">
              Ingresa el código
            </h1>
            <p className="text-sm text-muted-foreground mt-2 flex items-center justify-center gap-1">
              <Mail size={14} /> Enviado a <b className="text-foreground">{email}</b>
            </p>
          </div>

          <form
            onSubmit={submit}
            className="rounded-xl border border-border bg-white p-6 sm:p-7 space-y-5"
            data-testid="otp-form"
          >
            <div
              className="flex justify-between gap-2 sm:gap-3"
              onPaste={handlePaste}
              data-testid="otp-inputs"
            >
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputs.current[i] = el)}
                  className="otp-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={handleKey(i)}
                  data-testid={`otp-digit-${i}`}
                />
              ))}
            </div>

            {error && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm px-3 py-2"
                data-testid="otp-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || code.length !== OTP_LEN}
              className="w-full bg-[#2563EB] hover:bg-[#1e4fd0] text-white h-11"
              data-testid="otp-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Verificando…
                </>
              ) : (
                "Verificar código"
              )}
            </Button>

            <div className="flex items-center justify-between text-xs">
              <Link
                to={purpose === "login" ? "/login" : "/registro"}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1"
                data-testid="otp-back-link"
              >
                <ArrowLeft size={12} /> Volver
              </Link>
              <button
                type="button"
                onClick={resend}
                disabled={resending}
                className="text-[#2563EB] hover:underline font-medium flex items-center gap-1 disabled:opacity-50"
                data-testid="otp-resend-btn"
              >
                {resending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}
                Reenviar código
              </button>
            </div>

            <p className="text-[11px] text-muted-foreground text-center leading-relaxed pt-2 border-t border-border">
              El código expira en 10 minutos. Solo puedes intentar hasta 5 veces antes de que se
              invalide y necesites solicitar uno nuevo.
            </p>
          </form>
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}
